/**
 * MODULE: _shared/frontend-shell/src/hooks/territoryFetch.ts
 * PURPOSE: Standalone fetch for operations-service territories (flat hierarchy) for React Query caches
 * DEPENDENCIES: @shared/lib/frontend/api/config apiV1Url, ./useTerritories Territory type
 * PUBLIC_API: fetchAllTerritoriesFlat
 * SIDE_EFFECTS: GET /api/v1/territories (recursive by parent_id)
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 52/150
 */

import { apiV1Url } from '@shared/lib/frontend/api/config';
import type { Territory } from './useTerritories';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
});

/** Loads all territory rows for the tenant (roots + nested children). */
export async function fetchAllTerritoriesFlat(includeInactive = false): Promise<Territory[]> {
  const allTerritories: Territory[] = [];
  const visited = new Set<string>();
  const MAX_DEPTH = 12;

  const fetchLevel = async (parentId: string | null = null, depth = 0): Promise<void> => {
    if (depth > MAX_DEPTH) return;

    const params = new URLSearchParams();
    if (parentId) params.set('parent_id', parentId);
    if (includeInactive) params.set('include_inactive', 'true');

    const url = params.toString() ? `${apiV1Url('/territories')}?${params}` : apiV1Url('/territories');
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) return;

    const data = await response.json();
    const rows: Territory[] = data.territories || [];
    allTerritories.push(...rows);

    for (const territory of rows) {
      if (visited.has(territory.id)) continue;
      visited.add(territory.id);
      await fetchLevel(territory.id, depth + 1);
    }
  };

  await fetchLevel(null, 0);
  return allTerritories;
}
